/**
 * Cadastros → Marcas: nome, de onde vem o realizado (Winthor, SAP ou a definir),
 * ativa e ordem das colunas. "Nova marca" para quando entrar uma marca (ex.:
 * Kokeshi, Apice, Barbou's já estão cadastradas; uma marca nova entra aqui).
 * Atenção ao nome: ele reconhece a marca pelo nome do arquivo do Winthor ("fat amend
 * set.xlsx") e pela coluna Marca do relatório do SAP. Por isso, renomear pede
 * confirmação (as importações já feitas continuam ligadas à marca).
 *
 * Lê: os cadastros. Grava: api.salvarMarca.
 * Usado por: telas/Cadastros.tsx.
 */
import { useState } from 'react';
import { api } from '../../dados/api.ts';
import type { Fonte, Marca } from '../../dominio/tipos.ts';
import type { Executar } from './comum.ts';

type Rascunho = Omit<Marca, 'id'> & { id?: number };

function SeletorDeFonte({ valor, rotulo, aoMudar }: { valor: Fonte | null; rotulo: string; aoMudar: (fonte: Fonte | null) => void }) {
  return (
    <select aria-label={rotulo} value={valor ?? ''} onChange={(e) => aoMudar(e.target.value === '' ? null : (e.target.value as Fonte))}>
      <option value="winthor">Winthor (painel Aliança)</option>
      <option value="sap-bauny">SAP (painel Bauny)</option>
      <option value="">A definir</option>
    </select>
  );
}

export function ListaDeMarcas({ marcas, executar }: { marcas: Marca[]; executar: Executar }) {
  const [rascunhos, setRascunhos] = useState<Record<number, Marca>>({});
  const [nova, setNova] = useState<Rascunho | null>(null);
  const lista = [...marcas].sort((a, b) => a.ordem - b.ordem);
  const valor = (marca: Marca) => rascunhos[marca.id] ?? marca;
  const mudar = (marca: Marca, campos: Partial<Marca>) => setRascunhos((atual) => ({ ...atual, [marca.id]: { ...valor(marca), ...campos } }));
  const proximaOrdem = Math.max(0, ...marcas.map((m) => m.ordem)) + 1;

  async function salvar(marca: Marca) {
    const atual = valor(marca);
    if (atual.nome.trim() !== marca.nome) {
      const ok = window.confirm(
        `Renomear "${marca.nome}" para "${atual.nome.trim()}"? O sistema reconhece a marca pelo nome do arquivo do Winthor e pela coluna Marca do SAP: mude só se o nome mudou lá também.`,
      );
      if (!ok) return;
    }
    if (!(await executar(() => api.salvarMarca({ ...atual, nome: atual.nome.trim() }), `${atual.nome.trim()} salva.`))) return;
    setRascunhos((r) => {
      const { [marca.id]: _salva, ...resto } = r;
      return resto;
    });
  }

  async function criar() {
    if (!nova) return;
    if (await executar(() => api.salvarMarca({ nome: nova.nome.trim(), fonte: nova.fonte, ativa: nova.ativa, ordem: nova.ordem }), `Marca ${nova.nome.trim()} cadastrada.`)) setNova(null);
  }

  return (
    <section className="cartao pilha">
      <div className="pagina-titulo">
        <div>
          <h2>Marcas</h2>
          <p className="subtitulo">"De onde vem o realizado" decide em qual painel a meta da marca entra. Marca inativa não aparece nem conta.</p>
        </div>
        <button type="button" className="botao-secundario" onClick={() => setNova({ nome: '', fonte: 'winthor', ativa: true, ordem: proximaOrdem })} disabled={nova !== null}>
          Nova marca
        </button>
      </div>
      {nova ? (
        <div className="antes-depois">
          <label>
            Nome
            <input value={nova.nome} onChange={(e) => setNova({ ...nova, nome: e.target.value })} autoFocus />
          </label>
          <label>
            De onde vem o realizado
            <SeletorDeFonte valor={nova.fonte} rotulo="Fonte da marca nova" aoMudar={(fonte) => setNova({ ...nova, fonte })} />
          </label>
          <button type="button" className="botao" onClick={() => void criar()} disabled={nova.nome.trim() === ''}>
            Cadastrar
          </button>
          <button type="button" className="botao-secundario" onClick={() => setNova(null)}>
            Cancelar
          </button>
        </div>
      ) : null}
      <div className="tabela-rolavel">
        <table className="tabela">
          <thead>
            <tr>
              <th>Marca</th>
              <th>De onde vem o realizado</th>
              <th>Ativa</th>
              <th className="numero">Ordem</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((marca) => {
              const atual = valor(marca);
              const mudou = rascunhos[marca.id] !== undefined;
              return (
                <tr key={marca.id}>
                  <td>
                    <div className="linha-campos acoes-da-linha">
                      <input aria-label={`Nome da marca ${marca.nome}`} value={atual.nome} onChange={(e) => mudar(marca, { nome: e.target.value })} className="campo-nome-marca" />
                      {mudou ? (
                        <button type="button" className="botao" disabled={atual.nome.trim() === ''} onClick={() => void salvar(marca)}>
                          Salvar
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <SeletorDeFonte valor={atual.fonte} rotulo={`Fonte da ${marca.nome}`} aoMudar={(fonte) => mudar(marca, { fonte })} />
                  </td>
                  <td>
                    <input type="checkbox" aria-label={`${marca.nome} ativa`} checked={atual.ativa} onChange={(e) => mudar(marca, { ativa: e.target.checked })} />
                  </td>
                  <td className="numero">
                    <input type="number" aria-label={`Ordem da ${marca.nome}`} value={atual.ordem} style={{ width: 72 }} onChange={(e) => mudar(marca, { ordem: Number(e.target.value) })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
